/**
 * Перенос `presentation/providers/app_state_provider.dart`.
 *
 * Riverpod в порте нет: `NotifierProvider<AppStateNotifier, AppState>` стал
 * хранилищем zustand. Методы нотифаера — действия хранилища, присваивание
 * `state = …` — вызов `set`. Соответствие вызовов:
 *
 *   `ref.watch(appStateProvider)`        -> `useAppState()`
 *   `ref.read(appStateProvider)`         -> `appStateStore.getState().appState`
 *   `ref.read(appStateProvider.notifier)` -> `appStateStore.getState()`
 *
 * Самообновление APK (`downloadUpdate` и `listenSelf(listener)`, который её
 * запускал) убрано вместе с остальной Android-частью, поэтому и подписки на
 * собственное состояние здесь нет.
 */

import { create, type StoreApi } from 'zustand';

import { AppRepositoryImpl } from '@/data/appRepositoryImpl';
import { personRepository } from '@/data/personRepositoryImpl';
import { appPackageInfoFromBuild } from '@/domain/models/appPackageInfo';
import { appStateCopyWith, type AppState } from '@/domain/models/appState';
import type { ConfigModel } from '@/domain/models/configModel';
import type { PersonModel } from '@/domain/models/personModel';
import { EasyLoading } from '@/presentation/widgets/loading/loadingOverlay';
import { SnackbarManager } from '@/presentation/widgets/snackbar/snackbarManager';
import {
  AppException,
  AppExceptionType,
  stackTraceOf,
} from '@/utils/exceptions/appException';
import { AppFailure, FailureOp } from '@/utils/exceptions/appFailure';
import { getBaseClient } from '@/utils/http/httpClient';
import { Nullable } from '@/utils/nullable';
import { StorageHelper } from '@/utils/storageHelper';

/** Состояние и действия хранилища — то же, что было у `AppStateNotifier`. */
export interface AppStateStore {
  /** `ref.watch(appStateProvider)`. */
  readonly appState: AppState;

  /**
   * Чем кончилась последняя проверка адреса; `null` — успехом или адреса
   * нет. Экран языка по нему решает, отправлять ли на экран адреса.
   *
   * В Dart это было обычное поле нотифаера, а не состояние: перерисовку оно
   * не вызывало. Читать его следует так же — разово после
   * `await checkAndSetBaseUrl()` через `appStateStore.getState()`.
   */
  readonly lastConfigFailure: AppFailure | null;

  /**
   * Замена `appStateProvider.overrideWith(() => AppStateNotifier(initial))`
   * из `main.dart`: состояние, собранное стартом приложения, кладётся в уже
   * существующее хранилище.
   */
  readonly initAppState: (initialState: AppState) => void;

  readonly checkAndSetBaseUrl: (overrideBaseUrl?: string | null) => Promise<boolean>;

  readonly changePerson: (person: PersonModel) => Promise<boolean>;

  readonly refreshState: () => Promise<void>;
}

type SetAppStateStore = StoreApi<AppStateStore>['setState'];

/** Локальная функция `checkBaseUrl` из `fetchAppState`. */
async function checkBaseUrl(
  appRepo: AppRepositoryImpl,
  baseUrl: string | null,
  initial: boolean,
): Promise<ConfigModel | null> {
  try {
    if (baseUrl === null) {
      return null;
    }
    return await appRepo.fetchConfig(baseUrl);
  } catch (e) {
    if (
      e instanceof AppException &&
      e.type === AppExceptionType.connectionError &&
      initial
    ) {
      return null;
    }
    // `rethrow` — ошибка уходит дальше как есть, без перепаковки.
    throw e;
  }
}

/** Локальная функция `checkPerson` из `fetchAppState`. */
function checkPerson(
  config: ConfigModel | null,
  overrideBaseUrl: string | null,
): PersonModel | null {
  if (config === null) {
    return null;
  } else {
    const storage = StorageHelper.instance;
    // Проверяется другой планшет: сохранённый id — от прежнего, а id
    // персон у планшетов пересекаются. setBaseUrl сотрёт его следом.
    const otherTablet =
      overrideBaseUrl !== null && !storage.isCurrentBaseUrl(overrideBaseUrl);
    const id = otherTablet ? null : storage.person;

    if (id === null) {
      return null;
    } else {
      const persons = config.persons;
      const personIndex = persons.findIndex((element) => {
        return element.id === id;
      });
      if (personIndex < 0) return null;
      return persons[personIndex];
    }
  }
}

/**
 * Перенос статического `AppStateNotifier.fetchAppState`. Статическим он был
 * потому, что его зовут и из хранилища, и из старта приложения
 * (`bootstrapApp`), — здесь это обычная функция модуля.
 *
 * `PackageInfo.fromPlatform()` заменён на [appPackageInfoFromBuild]:
 * package_info_plus убран, версия приходит от сборки.
 */
export async function fetchAppState(
  initial: boolean,
  overrideBaseUrl?: string | null,
): Promise<AppState> {
  const appRepo = new AppRepositoryImpl({ client: getBaseClient() });
  // В Dart необязательный параметр был `String?`; здесь он ещё и
  // `undefined` (аргумент не передали) — оба случая значат «адрес не
  // подменяют».
  const override = overrideBaseUrl ?? null;
  const baseUrl = override ?? StorageHelper.instance.baseUrl;
  const config = await checkBaseUrl(appRepo, baseUrl, initial);
  const isAvailable = config !== null;
  const person = checkPerson(config, override);
  const packageInfo = appPackageInfoFromBuild();
  return {
    packageInfo: packageInfo,
    config: config,
    person: person,
    baseUrl:
      baseUrl === null
        ? null
        : {
            baseUrl: baseUrl,
            isAvailable: isAvailable,
          },
  };
}

/**
 * Состояние до старта.
 *
 * В Dart нотифаер создавался уже с результатом `_initialize`
 * (`appStateProvider.overrideWith`), и пустого состояния не существовало.
 * Здесь хранилище живёт с момента импорта, поэтому до [AppStateStore.initAppState]
 * в нём лежит заготовка: ни адреса, ни конфигурации, ни аватара. Страницы её
 * не видят — их рисуют после `bootstrapApp()`.
 */
function emptyAppState(): AppState {
  return {
    packageInfo: appPackageInfoFromBuild(),
    config: null,
    person: null,
    baseUrl: null,
  };
}

/** Перенос приватного `_changePerson` — сообщение серверу о смене аватара. */
async function changePersonOnServer(person: PersonModel): Promise<void> {
  try {
    await personRepository.changePerson(person);
  } catch (e) {
    SnackbarManager.instance.showFailure(
      AppFailure.fromException(FailureOp.personChange, e, stackTraceOf(e)),
    );
  }
}

async function checkAndSetBaseUrlAction(
  set: SetAppStateStore,
  overrideBaseUrl?: string | null,
): Promise<boolean> {
  set({ lastConfigFailure: null });
  try {
    const newState = await fetchAppState(false, overrideBaseUrl);
    const resultUrl = newState.baseUrl;
    if (resultUrl === null || !resultUrl.isAvailable) {
      return false;
    }
    if (overrideBaseUrl !== undefined && overrideBaseUrl !== null) {
      await StorageHelper.instance.setBaseUrl(overrideBaseUrl);
    }
    set({ appState: newState });
    return true;
  } catch (e) {
    const failure = AppFailure.fromException(FailureOp.config, e, stackTraceOf(e));
    set({ lastConfigFailure: failure });
    SnackbarManager.instance.showFailure(failure);
    return false;
  }
}

async function changePersonAction(
  set: SetAppStateStore,
  person: PersonModel,
): Promise<boolean> {
  try {
    EasyLoading.dismiss();
    EasyLoading.show();
    // Ответ сервера намеренно не ждут: выбор аватара не должен упираться в
    // сеть. Сбой запроса покажет свой тост — [changePersonOnServer] ловит
    // его сам, поэтому необработанного отказа промиса тут не будет.
    void changePersonOnServer(person);
    await StorageHelper.instance.setPerson(person.id);
    set((store) => {
      return {
        appState: appStateCopyWith(store.appState, {
          person: new Nullable(person),
        }),
      };
    });
    return true;
  } catch (e) {
    SnackbarManager.instance.showFailure(
      AppFailure.fromException(FailureOp.personChange, e, stackTraceOf(e)),
    );
    return false;
  } finally {
    EasyLoading.dismiss();
  }
}

/** Хранилище состояния приложения. */
export const useAppStateStore = create<AppStateStore>()((set) => {
  return {
    appState: emptyAppState(),
    lastConfigFailure: null,

    initAppState: (initialState: AppState): void => {
      set({ appState: initialState });
    },

    checkAndSetBaseUrl: (overrideBaseUrl?: string | null): Promise<boolean> => {
      return checkAndSetBaseUrlAction(set, overrideBaseUrl);
    },

    changePerson: (person: PersonModel): Promise<boolean> => {
      return changePersonAction(set, person);
    },

    refreshState: async (): Promise<void> => {
      set({ appState: await fetchAppState(false) });
    },
  };
});

/**
 * Хранилище вне React: контроллеры и менеджеры в Dart читали провайдер через
 * `ref.read`, а не через виджет. Это тот же объект, что и [useAppStateStore];
 * отдельное имя — чтобы вызов вне компонента не выглядел вызовом хука.
 */
export const appStateStore = useAppStateStore;

/** `ref.watch(appStateProvider)` — перерисовка при смене состояния. */
export function useAppState(): AppState {
  return useAppStateStore((store) => store.appState);
}
